import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { requireAdmin } from "@/lib/admin-guard";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useMemo, useState } from "react";
import {
  getBroadcast,
  saveBroadcast,
  sendTestBroadcast,
  sendBroadcast,
  previewAudienceCount,
  listBroadcastRecipients,
  retryFailedBroadcast,
  resumeBroadcast,
  getBroadcastCampaignStats,
} from "@/lib/broadcasts.functions";
import {
  ArrowLeft,
  Bold,
  Italic,
  Link as LinkIcon,
  Image as ImageIcon,
  MousePointerClick,
  Save,
  Send,
  Eye,
  Mail,
  Loader2,
  RefreshCw,
  CheckCircle2,
  AlertTriangle,
  Sparkles,
  Download,
} from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/admin_/broadcasts_/$id")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Edit Broadcast — Admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  beforeLoad: requireAdmin,
  component: BroadcastEditor,
});

const AUDIENCES: { value: string; label: string; desc: string }[] = [
  {
    value: "members",
    label: "Members Only",
    desc: "Everyone with active paid membership access right now. Safe for workshop announcements, Zoom links, member-only events and new member features. Excludes anyone who unsubscribed or opted out of marketing.",
  },
  {
    value: "registered_no_active_paid",
    label: "Non-Members Only",
    desc: "Confirmed, opted-in subscribers without active paid access right now, including waiting-list contacts and registered unpaid accounts. Excludes members, admins, test accounts, unsubscribers, suppressions and marketing opt-outs.",
  },
  {
    value: "cancelled_members",
    label: "Cancelled members",
    desc: "Only people who cancelled their membership, including those still inside a paid period. Anyone with a live, renewing membership is never included. Excludes admins, unsubscribers and marketing opt-outs.",
  },
  {
    value: "cold_members",
    label: "Cold members",
    desc: "People who previously bought something or visited but never signed up as a member. Excludes current members, admins, unsubscribers and marketing opt-outs.",
  },
];




/**
 * Sentinel returned when a send/resume request outlives the client's patience.
 *
 * The server-side engine caps each invocation at one batch (60 recipients or a
 * 45s deadline) and always persists progress before returning, so a request
 * that never answers — a recycled worker, a dropped mobile connection — has
 * NOT lost work. Resolving with this sentinel lets the button stop spinning and
 * hand over to the status poller instead of hanging indefinitely.
 */
const SEND_TIMED_OUT = Symbol("send-timed-out");

/** Batch deadline (45s) plus headroom for request/response overhead. */
const SEND_TIMEOUT_MS = 75_000;

function withSendTimeout<T>(p: Promise<T>): Promise<T | typeof SEND_TIMED_OUT> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => resolve(SEND_TIMED_OUT), SEND_TIMEOUT_MS);
    p.then(
      (v) => {
        clearTimeout(timer);
        resolve(v);
      },
      (e) => {
        clearTimeout(timer);
        reject(e);
      },
    );
  });
}


function BroadcastEditor() {
  const { id } = Route.useParams();
  const isNew = id === "new";
  const navigate = useNavigate();
  const qc = useQueryClient();

  const getFn = useServerFn(getBroadcast);
  const saveFn = useServerFn(saveBroadcast);
  const testFn = useServerFn(sendTestBroadcast);
  const sendFn = useServerFn(sendBroadcast);
  const countFn = useServerFn(previewAudienceCount);
  const listRecipientsFn = useServerFn(listBroadcastRecipients);
  const retryFn = useServerFn(retryFailedBroadcast);
  const resumeFn = useServerFn(resumeBroadcast);
  const campaignStatsFn = useServerFn(getBroadcastCampaignStats);

  const { data: existing, isLoading } = useQuery({
    queryKey: ["broadcast", id],
    queryFn: () => getFn({ data: { id } }),
    enabled: !isNew,
    refetchInterval: (q) => {
      const s = (q.state.data as any)?.broadcast?.status;
      return s === "sending" || s === "scheduled" ? 3000 : false;
    },
  });

  // Mirrors STALE_SENDING_MS in broadcasts-internal.server.ts: a 'sending' row
  // whose heartbeat is older than this was killed mid-batch, and the server
  // will take the lock over. Showing it as "in flight" would spin forever.
  const STALE_SENDING_MS = 5 * 60 * 1000;
  const rawStatus = existing?.broadcast?.status;
  const lastBeat = existing?.broadcast?.last_attempt_at
    ? new Date(existing.broadcast.last_attempt_at).getTime()
    : 0;
  const isStalled = rawStatus === "sending" && Date.now() - lastBeat > STALE_SENDING_MS;

  const isActivelySending =
    !isStalled && (rawStatus === "sending" || rawStatus === "scheduled");

  const { data: recipientsData, refetch: refetchRecipients } = useQuery({
    queryKey: ["broadcast-recipients", id],
    queryFn: () => listRecipientsFn({ data: { id } }),
    enabled: !isNew && !!existing?.broadcast && existing.broadcast.status !== "draft",
    refetchInterval: isActivelySending ? 3000 : false,
    refetchIntervalInBackground: false,
  });

  const { data: campaignStats, refetch: refetchCampaignStats } = useQuery({
    queryKey: ["broadcast-campaign-stats", id],
    queryFn: () => campaignStatsFn({ data: { id } }),
    enabled: !isNew && !!existing?.broadcast && existing.broadcast.status !== "draft",
    refetchInterval: isActivelySending ? 5000 : false,
  });

  // The send engine intentionally processes at most one batch (60 recipients /
  // 45s) per invocation and leaves status='sending' for the cron dispatcher to
  // resume. A spinner bound only to the local mutation therefore either spins
  // forever (if the HTTP call is dropped by a worker recycle or a flaky mobile
  // connection) or lies after a reload. Treat the polled server status as the
  // source of truth and use local pending only for the brief pre-ack window.
  const remaining = campaignStats?.remaining ?? 0;
  const eligible = campaignStats?.eligible ?? 0;
  const alreadySent = campaignStats?.already_sent ?? 0;

  // (sendBusy / resumeBusy are derived below, once the mutations are declared.)

  const [subject, setSubject] = useState("");
  const [preheader, setPreheader] = useState("");
  const [audience, setAudience] = useState("members");
  const [htmlBody, setHtmlBody] = useState(
    `<p>Hi friend,</p>\n<p>Write your message here. You can use <strong>bold</strong>, <em>italics</em>, links, images and buttons from the toolbar.</p>\n<p>— Jana</p>`,
  );
  const [scheduleAt, setScheduleAt] = useState("");
  const [savedId, setSavedId] = useState<string | null>(isNew ? null : id);
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    if (existing?.broadcast) {
      const b = existing.broadcast;
      setSubject(b.subject ?? "");
      setPreheader(b.preheader ?? "");
      setAudience(b.audience ?? "members");
      setHtmlBody(b.html_body ?? "");
      setScheduleAt(b.scheduled_for ? new Date(b.scheduled_for).toISOString().slice(0, 16) : "");
      setSavedId(b.id);
    }
  }, [existing]);

  const { data: audienceCount } = useQuery({
    queryKey: ["audience-count", audience],
    queryFn: () => countFn({ data: { audience: audience as any } }),
  });

  const save = useMutation({
    mutationFn: async () => {
      const r = await saveFn({
        data: {
          id: savedId ?? undefined,
          subject: subject.trim(),
          preheader: preheader.trim() || null,
          html_body: htmlBody,
          audience: audience as any,
          scheduled_for: scheduleAt ? new Date(scheduleAt).toISOString() : null,
        },
      });
      return r.broadcast;
    },
    onSuccess: (b) => {
      toast.success("Saved");
      if (b?.id && !savedId) {
        setSavedId(b.id);
        navigate({ to: "/admin/broadcasts/$id", params: { id: b.id }, replace: true });
      }
      qc.invalidateQueries({ queryKey: ["broadcasts"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  async function ensureSaved(): Promise<string> {
    if (savedId) {
      // Save latest edits before send so recipients get the current content.
      await save.mutateAsync();
      return savedId;
    }
    const b = await save.mutateAsync();
    if (!b?.id) throw new Error("Could not save broadcast");
    return b.id;
  }

  const sendTest = useMutation({
    mutationFn: async () => {
      const to = prompt("Send test email to:", "");
      if (!to) return null;
      const id2 = await ensureSaved();
      return testFn({ data: { id: id2, to } });
    },
    onSuccess: (r) => {
      if (r) toast.success("Test email sent");
    },
    onError: (e: Error) => toast.error(e.message || "Failed to send test"),
  });

  const sendNow = useMutation({
    mutationFn: async () => {
      const ok = confirm(
        `Audience: ${AUDIENCES.find((a) => a.value === audience)?.label ?? audience}\nRecipients: ${audienceCount?.count ?? "?"}\n\nSend this broadcast now? This cannot be undone.`,
      );
      if (!ok) return null;
      const id2 = await ensureSaved();
      // Never let the button hang on a request the worker may never answer.
      // The send is already durable server-side, so on timeout we hand over to
      // the status poller rather than leaving a spinner running forever.
      return withSendTimeout(sendFn({ data: { id: id2 } }));
    },
    onSuccess: (r) => {
      if (!r) return;
      if (r === SEND_TIMED_OUT) {
        toast.info("Still sending — tracking progress below.");
      } else if (r.sent > 0 && r.failed === 0) {
        toast.success(`Sent to ${r.sent} recipient${r.sent === 1 ? "" : "s"} in this batch`);
      } else if (r.sent > 0) {
        toast.warning(`Sent ${r.sent} · ${r.failed} failed`);
      } else if (r.failed > 0) {
        toast.error(`Send failed for all ${r.failed} recipients`);
      }
      qc.invalidateQueries({ queryKey: ["broadcasts"] });
      qc.invalidateQueries({ queryKey: ["broadcast", id] });
      qc.invalidateQueries({ queryKey: ["broadcast-recipients", id] });
      qc.invalidateQueries({ queryKey: ["broadcast-campaign-stats", id] });
    },
    onError: (e: Error) => toast.error(e.message || "Failed to send broadcast"),
  });

  const retry = useMutation({
    mutationFn: async () => {
      const ok = confirm(
        `Resend to the ${recipientsData?.summary.failed ?? 0} recipients that previously failed?`,
      );
      if (!ok) return null;
      return retryFn({ data: { id: savedId ?? id } });
    },
    onSuccess: (r) => {
      if (!r) return;
      if (r.sent > 0 && r.failed === 0) toast.success(`Resent ${r.sent}`);
      else if (r.sent > 0) toast.warning(`Resent ${r.sent} · ${r.failed} still failed`);
      else toast.error(`All ${r.failed} still failing — likely provider quota`);
      qc.invalidateQueries({ queryKey: ["broadcast", id] });
      qc.invalidateQueries({ queryKey: ["broadcast-recipients", id] });
      refetchRecipients();
    },
    onError: (e: Error) => toast.error(e.message || "Retry failed"),
  });

  const resumeNow = useMutation({
    mutationFn: async () => {
      const left = campaignStats?.remaining ?? 0;
      const ok = confirm(
        `Send this campaign to the ${left} remaining recipient${left === 1 ? "" : "s"} who haven't received it yet? Anyone already delivered will be skipped.`,
      );
      if (!ok) return null;
      return withSendTimeout(resumeFn({ data: { id: savedId ?? id } }));
    },
    onSuccess: (r) => {
      if (!r) return;
      if (r === SEND_TIMED_OUT) {
        toast.info("Still sending — tracking progress below.");
      } else {
        toast.success(`Sent ${r.sent ?? 0} · ${r.failed ?? 0} failed`);
      }
      qc.invalidateQueries({ queryKey: ["broadcast", id] });
      qc.invalidateQueries({ queryKey: ["broadcast-recipients", id] });
      qc.invalidateQueries({ queryKey: ["broadcast-campaign-stats", id] });
      refetchRecipients();
      refetchCampaignStats();
    },
    onError: (e: Error) => toast.error(e.message || "Resume failed"),
  });

  const scheduleResume = useMutation({
    mutationFn: async () => {
      if (!scheduleAt) {
        toast.error("Pick a schedule time first (below the Audience panel).");
        return null;
      }
      const iso = new Date(scheduleAt).toISOString();
      const left = campaignStats?.remaining ?? 0;
      const ok = confirm(
        `Schedule this campaign to send to the ${left} remaining recipient${left === 1 ? "" : "s"} at ${new Date(iso).toLocaleString()}?`,
      );
      if (!ok) return null;
      return resumeFn({ data: { id: savedId ?? id, scheduled_for: iso } });
    },
    onSuccess: (r) => {
      if (!r) return;
      toast.success("Scheduled — the cron dispatcher will pick this up.");
      qc.invalidateQueries({ queryKey: ["broadcasts"] });
      qc.invalidateQueries({ queryKey: ["broadcast", id] });
    },
    onError: (e: Error) => toast.error(e.message || "Schedule failed"),
  });

  // Server status is authoritative: a run left in 'sending' by a killed worker
  // or an unanswered request must still read as busy after a reload.
  const sendBusy = sendNow.isPending || isActivelySending;
  const resumeBusy = resumeNow.isPending || isActivelySending;





  function insertAtCursor(text: string) {
    const ta = document.getElementById("html-body") as HTMLTextAreaElement | null;
    if (!ta) {
      setHtmlBody((prev) => prev + text);
      return;
    }
    const start = ta.selectionStart ?? htmlBody.length;
    const end = ta.selectionEnd ?? htmlBody.length;
    const next = htmlBody.slice(0, start) + text + htmlBody.slice(end);
    setHtmlBody(next);
    setTimeout(() => {
      ta.focus();
      ta.selectionStart = ta.selectionEnd = start + text.length;
    }, 0);
  }

  function wrapSelection(before: string, after: string) {
    const ta = document.getElementById("html-body") as HTMLTextAreaElement | null;
    if (!ta) return insertAtCursor(`${before}${after}`);
    const start = ta.selectionStart ?? 0;
    const end = ta.selectionEnd ?? 0;
    const sel = htmlBody.slice(start, end) || "text";
    const next = htmlBody.slice(0, start) + before + sel + after + htmlBody.slice(end);
    setHtmlBody(next);
  }

  const previewHtml = useMemo(
    () =>
      `<html><body style="font-family:Arial,sans-serif;color:#1a1a1a;padding:24px;max-width:600px;margin:0 auto">${htmlBody}<hr style="margin:24px 0;border:none;border-top:1px solid #eee"/><p style="font-size:12px;color:#888;text-align:center">Unsubscribe link will appear here · Recovery Trauma™</p></body></html>`,
    [htmlBody],
  );

  if (!isNew && isLoading) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-16 text-center">
        <Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-8">
      <div className="mb-6">
        <Link
          to="/admin/broadcasts"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> All broadcasts
        </Link>
        <h1 className="mt-2 flex items-center gap-2 font-serif text-3xl text-foreground">
          <Mail className="h-7 w-7 text-[var(--gold-deep)]" />
          {isNew ? "New broadcast" : "Edit broadcast"}
        </h1>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <div className="rounded-2xl border border-border bg-card p-5">
            <label className="block text-sm font-medium text-foreground">Subject</label>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={300}
              placeholder="Your launch announcement"
              className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
            />
            <label className="mt-4 block text-sm font-medium text-foreground">
              Preheader <span className="text-muted-foreground">(short preview text)</span>
            </label>
            <input
              value={preheader}
              onChange={(e) => setPreheader(e.target.value)}
              maxLength={300}
              className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
            />
          </div>

          <div className="rounded-2xl border border-border bg-card p-5">
            <div className="mb-3 flex items-center justify-between gap-2">
              <label className="text-sm font-medium text-foreground">Email body (HTML)</label>
              <button
                onClick={() => setPreviewing((v) => !v)}
                className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs hover:bg-muted"
              >
                <Eye className="h-3.5 w-3.5" /> {previewing ? "Edit" : "Preview"}
              </button>
            </div>
            <div className="mb-2 flex flex-wrap gap-1 rounded-lg border border-border bg-muted/30 p-1">
              <ToolbarBtn onClick={() => wrapSelection("<strong>", "</strong>")} icon={Bold} label="Bold" />
              <ToolbarBtn onClick={() => wrapSelection("<em>", "</em>")} icon={Italic} label="Italic" />
              <ToolbarBtn
                onClick={() => {
                  const url = prompt("Link URL", "https://");
                  if (url) wrapSelection(`<a href="${url}" style="color:#c9a14a">`, "</a>");
                }}
                icon={LinkIcon}
                label="Link"
              />
              <ToolbarBtn
                onClick={() => {
                  const url = prompt("Image URL", "https://");
                  if (url)
                    insertAtCursor(
                      `<img loading="lazy" decoding="async" src="${url}" alt="" style="max-width:100%;height:auto;border-radius:8px;margin:12px 0"/>`,
                    );
                }}
                icon={ImageIcon}
                label="Image"
              />
              <ToolbarBtn
                onClick={() => {
                  const url = prompt("Button URL", "https://");
                  const text = prompt("Button text", "Click here");
                  if (url && text)
                    insertAtCursor(
                      `\n<p style="text-align:center;margin:24px 0"><a href="${url}" style="display:inline-block;background:#c9a14a;color:#fff;padding:12px 24px;border-radius:8px;font-weight:600;text-decoration:none">${text}</a></p>\n`,
                    );
                }}
                icon={MousePointerClick}
                label="Button"
              />
              <ToolbarBtn
                onClick={() => insertAtCursor("\n<h2 style=\"margin:24px 0 8px\">Heading</h2>\n")}
                label="H2"
              />
              <ToolbarBtn onClick={() => insertAtCursor("\n<p></p>\n")} label="¶" />
            </div>
            {previewing ? (
              <iframe
                title="Preview"
                srcDoc={previewHtml}
                sandbox=""
                className="h-[480px] w-full rounded-lg border border-border bg-white"
              />
            ) : (
              <textarea
                id="html-body"
                value={htmlBody}
                onChange={(e) => setHtmlBody(e.target.value)}
                className="h-[480px] w-full rounded-lg border border-input bg-background p-3 font-mono text-xs"
              />
            )}
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded-2xl border border-border bg-card p-5">
            <label className="block text-sm font-medium text-foreground">Audience</label>
            <select
              value={audience}
              onChange={(e) => setAudience(e.target.value)}
              className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
            >
              {AUDIENCES.map((a) => (
                <option key={a.value} value={a.value}>
                  {a.label}
                </option>
              ))}
            </select>
            <p className="mt-2 text-xs text-muted-foreground">
              {AUDIENCES.find((a) => a.value === audience)?.desc}
            </p>
            <div className="mt-3 rounded-lg bg-muted/40 px-3 py-2 text-sm">
              Estimated recipients:{" "}
              <strong className="text-foreground">{audienceCount?.count ?? "…"}</strong>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              Unsubscribed users and those without marketing consent are always excluded.
            </p>
          </div>

          <div className="rounded-2xl border border-border bg-card p-5">
            <label className="block text-sm font-medium text-foreground">
              Schedule (optional)
            </label>
            <input
              type="datetime-local"
              value={scheduleAt}
              onChange={(e) => setScheduleAt(e.target.value)}
              className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
            />
            <p className="mt-2 text-xs text-muted-foreground">
              Leave blank to send manually with "Send now".
            </p>
          </div>

          <div className="space-y-2 rounded-2xl border border-border bg-card p-5">
            <button
              onClick={() => save.mutate()}
              disabled={save.isPending || !subject.trim()}
              className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-border bg-background px-4 py-2 text-sm font-medium hover:bg-muted disabled:opacity-50"
            >
              <Save className="h-4 w-4" /> Save draft
            </button>
            <button
              onClick={() => sendTest.mutate()}
              disabled={sendTest.isPending || !subject.trim()}
              className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-border bg-background px-4 py-2 text-sm font-medium hover:bg-muted disabled:opacity-50"
            >
              <Mail className="h-4 w-4" /> Send test email
            </button>
            <button
              onClick={() => sendNow.mutate()}
              disabled={sendBusy || !subject.trim()}
              className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[var(--gold-deep)] px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
            >
              {sendBusy ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              {sendBusy
                ? eligible > 0
                  ? `Sending… ${alreadySent} of ${eligible}`
                  : "Sending…"
                : "Send now"}
            </button>
            <p className="pt-2 text-center text-xs text-muted-foreground">
              All sends include an unsubscribe link (GDPR / CAN-SPAM compliant).
            </p>
          </div>
        </div>
      </div>

      {campaignStats && (
        <div className="mt-8 rounded-2xl border border-[var(--gold-deep)]/30 bg-[var(--gold-deep)]/5 p-5">
          {isStalled && (
            <div className="mb-4 flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-900 dark:text-amber-200">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                This send stopped part-way through — the worker was interrupted, not the
                campaign. Nothing was lost and nobody was emailed twice.{" "}
                {remaining > 0
                  ? `Press “Resume now” to send the ${remaining} still waiting.`
                  : "Every recipient has already been delivered to."}
              </span>
            </div>
          )}
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="flex items-center gap-2 font-serif text-xl text-foreground">
                <Sparkles className="h-5 w-5 text-[var(--gold-deep)]" /> Campaign progress
              </h2>
              <p className="mt-1 text-xs text-muted-foreground">
                Cumulative results across every send-attempt of this broadcast. Recipients
                already delivered to are skipped automatically when you resume.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => scheduleResume.mutate()}
                disabled={scheduleResume.isPending || remaining === 0}
                className="inline-flex items-center gap-2 rounded-lg border border-[var(--gold-deep)]/50 bg-background px-3 py-2 text-sm font-medium text-[var(--gold-deep)] hover:bg-[var(--gold-deep)]/10 disabled:opacity-50"
                title={scheduleAt ? `Schedule for ${new Date(scheduleAt).toLocaleString()}` : "Set a schedule time in the Schedule panel first"}
              >
                {scheduleResume.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Schedule resume{scheduleAt ? ` · ${new Date(scheduleAt).toLocaleString()}` : ""}
              </button>
              <button
                onClick={() => resumeNow.mutate()}
                disabled={resumeBusy || remaining === 0}
                className="inline-flex items-center gap-2 rounded-lg bg-[var(--gold-deep)] px-3 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
              >
                {resumeBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                {resumeBusy
                  ? `Sending… ${alreadySent} of ${eligible}`
                  : `Resume now — send ${remaining} remaining`}
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
            <RecipStat label="Eligible" value={campaignStats.eligible} tone="gold" />
            <RecipStat label="Already sent" value={campaignStats.already_sent} tone="ok" />
            <RecipStat label="Remaining" value={campaignStats.remaining} tone={(campaignStats.remaining ?? 0) > 0 ? "warn" : "muted"} />
            <RecipStat label="Delivered" value={campaignStats.delivered} tone="ok" />
            <RecipStat label="Opened" value={campaignStats.opened} tone="muted" />
            <RecipStat label="Clicked" value={campaignStats.clicked} tone="muted" />
            <RecipStat label="Unsubscribed" value={campaignStats.unsubscribed} tone="muted" />
            <RecipStat label="Bounced" value={campaignStats.bounced} tone={campaignStats.bounced ? "warn" : "muted"} />
            <RecipStat label="Failed" value={campaignStats.failed} tone={campaignStats.failed ? "warn" : "muted"} />
          </div>
        </div>
      )}

      {recipientsData && (
        <SendProgressDashboard
          summary={recipientsData.summary}
          isActive={isActivelySending}
        />
      )}

      {recipientsData && (
        <div className="mt-8 rounded-2xl border border-border bg-card p-5">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="flex items-center gap-2 font-serif text-xl text-foreground">
                <Mail className="h-5 w-5 text-[var(--gold-deep)]" /> Recipients ({recipientsData.summary.total})
              </h2>
              <p className="mt-1 text-xs text-muted-foreground">
                Every lead this broadcast was addressed to, whether delivery succeeded, and
                which recipients have since become paying Founding Members.
              </p>
            </div>
            {recipientsData.summary.failed > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={() => downloadFailureReport(recipientsData.rows, id)}
                  className="inline-flex items-center gap-2 rounded-lg border border-border bg-background px-3 py-2 text-sm font-medium text-foreground hover:bg-muted"
                >
                  <Download className="h-4 w-4" />
                  Download failure report
                </button>
                <button
                  onClick={() => retry.mutate()}
                  disabled={retry.isPending}
                  className="inline-flex items-center gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm font-medium text-amber-700 hover:bg-amber-500/20 disabled:opacity-50 dark:text-amber-300"
                >
                  {retry.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                  Retry {recipientsData.summary.failed} failed
                </button>
              </div>
            )}
          </div>

          <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-5">
            <RecipStat label="Sent" value={recipientsData.summary.sent} tone="ok" />
            <RecipStat label="Failed" value={recipientsData.summary.failed} tone={recipientsData.summary.failed ? "warn" : "muted"} />
            <RecipStat label="Pending" value={recipientsData.summary.pending} tone="muted" />
            <RecipStat label="Unsubscribed" value={recipientsData.summary.unsubscribed} tone="muted" />
            <RecipStat label="Paid Member" value={recipientsData.summary.paid_after} tone="gold" />
          </div>

          {recipientsData.summary.failed > 0 && (
            <div className="mb-4 rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 text-xs text-amber-900 dark:text-amber-200">
              <AlertTriangle className="me-1 inline h-3.5 w-3.5" />
              Most recent failures were <code className="rounded bg-black/5 px-1">daily_quota_exceeded</code>{" "}
              from Resend's daily send cap. Retry after the quota resets (00:00 UTC) or upgrade the
              Resend plan for a higher limit, then click "Retry failed".
            </div>
          )}

          <div className="max-h-[500px] overflow-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-muted/40 text-start text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-3 py-2">Email</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Sent</th>
                  <th className="px-3 py-2">Paid Founding Member</th>
                  <th className="px-3 py-2">Error</th>
                </tr>
              </thead>
              <tbody>
                {recipientsData.rows.map((r) => (
                  <tr key={r.id} className="border-t border-border/40 align-top">
                    <td className="px-3 py-2 font-mono text-xs break-all">{r.email}</td>
                    <td className="px-3 py-2">
                      <StatusPill status={r.status} />
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">
                      {r.sent_at ? new Date(r.sent_at).toLocaleString() : "—"}
                    </td>
                    <td className="px-3 py-2">
                      {r.is_paid_founding_member ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 dark:text-emerald-300">
                          <Sparkles className="h-3 w-3" />
                          Paid {r.paid_at ? `· ${new Date(r.paid_at).toLocaleDateString()}` : ""}
                        </span>
                      ) : r.membership_status ? (
                        <span className="text-[11px] text-muted-foreground">{r.membership_status}</span>
                      ) : (
                        <span className="text-[11px] text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-[11px] text-destructive/80 max-w-[280px] truncate" title={r.error ?? ""}>
                      {r.error ?? ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function downloadFailureReport(
  rows: Array<{ email: string; status: string; error?: string | null; sent_at?: string | null }>,
  broadcastId: string,
) {
  const failed = rows.filter((r) => r.status === "failed");
  const header = ["email", "status", "error", "last_attempt_at"];
  const escape = (v: string) => `"${(v ?? "").replace(/"/g, '""')}"`;
  const lines = [
    header.join(","),
    ...failed.map((r) =>
      [r.email, r.status, r.error ?? "", r.sent_at ?? ""].map((v) => escape(String(v))).join(","),
    ),
  ];
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const ts = new Date().toISOString().replace(/[:.]/g, "-");
  a.href = url;
  a.download = `broadcast-${broadcastId}-failures-${ts}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function SendProgressDashboard({
  summary,
  isActive,
}: {
  summary: {
    total: number;
    sent: number;
    failed: number;
    pending: number;
    unsubscribed: number;
  };
  isActive: boolean;
}) {
  const processed = summary.sent + summary.failed + summary.unsubscribed;
  const pct = summary.total > 0 ? Math.min(100, Math.round((processed / summary.total) * 100)) : 0;
  const retryable = summary.failed;
  return (
    <div className="mt-8 rounded-2xl border border-border bg-card p-5">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 font-serif text-xl text-foreground">
            <Send className="h-5 w-5 text-[var(--gold-deep)]" /> Send progress
            {isActive && (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 dark:text-emerald-300">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />
                LIVE
              </span>
            )}
          </h2>
          <p className="mt-1 text-xs text-muted-foreground">
            {isActive
              ? "Auto-refreshing every 3 seconds while the send is in flight."
              : "Snapshot of the last send. Refreshes automatically if a retry is running."}
          </p>
        </div>
        <div className="text-end">
          <div className="text-2xl font-semibold text-foreground">{pct}%</div>
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
            {processed} / {summary.total} processed
          </div>
        </div>
      </div>

      <div className="mb-4 h-2 w-full overflow-hidden rounded-full bg-muted">
        <div className="flex h-full w-full">
          <div
            className="h-full bg-emerald-500 transition-all"
            style={{ width: `${summary.total ? (summary.sent / summary.total) * 100 : 0}%` }}
          />
          <div
            className="h-full bg-amber-500 transition-all"
            style={{ width: `${summary.total ? (summary.failed / summary.total) * 100 : 0}%` }}
          />
          <div
            className="h-full bg-muted-foreground/40 transition-all"
            style={{ width: `${summary.total ? (summary.unsubscribed / summary.total) * 100 : 0}%` }}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <RecipStat label="Queued" value={summary.pending} tone={summary.pending ? "warn" : "muted"} />
        <RecipStat label="Sent" value={summary.sent} tone="ok" />
        <RecipStat label="Failed" value={summary.failed} tone={summary.failed ? "warn" : "muted"} />
        <RecipStat label="Retryable" value={retryable} tone={retryable ? "gold" : "muted"} />
      </div>
    </div>
  );
}

function RecipStat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: "ok" | "warn" | "muted" | "gold";
}) {
  const cls =
    tone === "ok"
      ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30"
      : tone === "warn"
        ? "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30"
        : tone === "gold"
          ? "bg-[var(--gold-deep)]/10 text-[var(--gold-deep)] border-[var(--gold-deep)]/30"
          : "bg-muted/40 text-muted-foreground border-border";
  return (
    <div className={`rounded-lg border px-3 py-2 ${cls}`}>
      <div className="text-[10px] uppercase tracking-wider opacity-80">{label}</div>
      <div className="text-lg font-semibold">{value}</div>
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const s = status.toLowerCase();
  const cls =
    s === "sent"
      ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
      : s === "failed"
        ? "bg-destructive/10 text-destructive"
        : s === "unsubscribed"
          ? "bg-slate-500/15 text-foreground dark:text-slate-300"
          : "bg-muted text-muted-foreground";
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold capitalize ${cls}`}>
      {status}
    </span>
  );
}


function ToolbarBtn({
  onClick,
  icon: Icon,
  label,
}: {
  onClick: () => void;
  icon?: any;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs text-foreground hover:bg-background"
    >
      {Icon && <Icon className="h-3.5 w-3.5" />}
      {!Icon && label}
    </button>
  );
}
